import { type ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands';
import { serializeDatabase } from '@databases/ai-first-creation-serializer';
import { collabStateManager } from '@databases/collab-state-manager';
import { viewState } from '@databases/database-state/state/view';
import { logError } from '@databases/logs';
import { selectSelectedCellsForSerialization } from '@databases/redux/cellSelections/selectors';
import { appStore } from '@databases/redux/store';
import { selectDatabaseNameByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { snapshot } from 'valtio';

import { DatabaseStreamState } from '../database-stream-state';

const streamState = DatabaseStreamState.getInstance();

export const readDatabaseContent = (
  contentId: string
): Promise<ReadContentCommandResult> => {
  try {
    const state = appStore.getState();
    const databaseName = selectDatabaseNameByContentIdOptional(state, { contentId });

    const manager = collabStateManager.getCollabManager(contentId);

    if (!manager) {
      throw new Error('Database not found');
    }

    const currentView = snapshot(viewState.view);
    const currentViewId = currentView.id || undefined;
    const selectedCells = selectSelectedCellsForSerialization(state, {
      contentId,
      database: manager.database,
    });

    const {
      output: csv,
      warnings,
      rowIdToEntryIdMappings,
    } = serializeDatabase(manager.database, selectedCells, currentViewId);

    if (warnings.hasWarnings()) {
      logError('Warnings during database serialization', new Error(warnings.toString()));
    }

    const title = databaseName || 'Untitled Database';
    const url = window.location.href;

    streamState.populateRowIdToEntryIdMappings(rowIdToEntryIdMappings);

    return {
      contentType: 'database',
      csv,
      title,
      url,
      contentId,
    } as any;
  } catch (possibleError) {
    const error =
      possibleError instanceof Error ? possibleError : new Error(String(possibleError));
    logError('Error reading database content', error);
    throw error;
  }
};
